import { FloatingLabel, Form, FormGroup } from 'react-bootstrap';
import React from 'react';
import {
    IAppMultipleChoiceRequirement,
    IAppRequirement,
} from 'interfaces/IApplicationRequirements';

interface IApplicationsRequirementsProps {
    name: string;
    value: any;
    requirement: IAppRequirement;
    handleChange: (name: string, value: any) => void;
    selectOptions?: { id: string; name: string }[];
    validated: boolean;
}

const ApplicationRequirementControl: React.FC<IApplicationsRequirementsProps> = ({
    name,
    value,
    requirement,
    handleChange,
    selectOptions = [],
    validated,
}) => {
    switch (requirement.type) {
        case 'checkbox':
            const isValid = !requirement.required || !!value;
            return (
                <FormGroup controlId={name}>
                    <Form.Label className={'d-flex'}>
                        <Form.Check
                            aria-label={requirement.title}
                            style={{ paddingRight: '1em' }}
                            required={requirement.required || false}
                            name={`experience.${name}`}
                            onChange={(e) => {
                                const { checked } = e.currentTarget;
                                handleChange(name, checked);
                            }}
                            checked={value !== undefined ? (value as boolean) : false}
                            isValid={validated && isValid}
                            isInvalid={validated && !isValid}
                        />
                        {requirement.label}
                    </Form.Label>
                </FormGroup>
            );
        case 'document':
            return (
                <FormGroup controlId={name}>
                    <FloatingLabel
                        label={requirement.title + (requirement.required ? ' (required)' : '')}
                    >
                        <Form.Select
                            aria-label={requirement.title}
                            name={`document.${name}`}
                            required={requirement.required || false}
                            value={value || ''}
                            onChange={(e) => handleChange(name, e.currentTarget.value)}
                            disabled={selectOptions.length === 0}
                            isValid={validated && requirement.required && !!value?.trim().length}
                            isInvalid={validated && requirement.required && !value?.trim().length}
                        >
                            <option value="">
                                {selectOptions.length > 0
                                    ? 'Select your document'
                                    : 'No documents available'}
                            </option>
                            {selectOptions.map((item) => (
                                <option
                                    value={item.id}
                                    key={`${requirement.name}-${name}-${item.id}`}
                                >
                                    {item.name}
                                </option>
                            ))}
                        </Form.Select>
                    </FloatingLabel>
                </FormGroup>
            );
        case 'multipleChoice':
            const mcRequirement = requirement as IAppMultipleChoiceRequirement;
            const multipleChoiceSelections: string[] = (value || []) as string[];
            const handleMultipleChoiceChange = (optionName: string, value: boolean) => {
                if (value) {
                    handleChange(name, multipleChoiceSelections.concat([optionName]));
                } else {
                    handleChange(
                        name,
                        multipleChoiceSelections.filter((item) => item !== optionName)
                    );
                }
            };

            return (
                <FormGroup>
                    {requirement.required}
                    {mcRequirement.choices.map((choice) => (
                        <ApplicationRequirementControl
                            value={multipleChoiceSelections.includes(choice.name)}
                            requirement={{
                                name: choice.name,
                                label: choice.label,
                                title: choice.label,
                                type: 'checkbox',
                                required: false,
                            }}
                            handleChange={(optionName: string, value: boolean) =>
                                handleMultipleChoiceChange(optionName, value)
                            }
                            validated={validated}
                            key={choice.name}
                            name={choice.name}
                        />
                    ))}
                </FormGroup>
            );
        default:
            const isValidNumber =
                (value as number) >= (requirement.min ? requirement.min : 0) &&
                (value as number) <= (requirement.max ? requirement.max : Number.MAX_SAFE_INTEGER);
            return (
                <FormGroup controlId={`experience.${name}`}>
                    <Form.Control
                        aria-label={requirement.title}
                        style={{ width: '90px', borderRadius: '5px' }}
                        type={requirement.type}
                        required={requirement.required}
                        name={`experience.${name}`}
                        min={requirement.min}
                        max={requirement.max}
                        onChange={(e) => handleChange(name, e.target.value)}
                        value={(value as number) || ''}
                        isInvalid={validated && !isValidNumber}
                        isValid={validated && isValidNumber}
                    />
                </FormGroup>
            );
    }
};

export default ApplicationRequirementControl;
